import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import type {
  BrandInterface,
  CountryInterface,
  UnitOfMeasurementInterface,
} from '@/types'
import {
  fetchBrands,
  fetchCategoriesTree,
  fetchCountries,
  fetchNomenclatureGroupsTree,
  fetchUnitsOfMeasurement,
} from '@/utils/api/fetch/fetchEntities'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'

import cn from '../SingularImport.module.scss'

const toAntdTree = (
  nodes: Parameters<typeof convertToAntdFormatTree>[0]['nodes']
) =>
  nodes.length > 0
    ? convertToAntdFormatTree({
        nodes,
        classNames: cn,
        allSelectable: false,
        inTreeSelect: true,
      })
    : []

export const useNomenclatureDictionaries = () => {
  const [brands, setBrands] = useState<BrandInterface[]>([])
  const [countries, setCountries] = useState<CountryInterface[]>([])
  const [units, setUnits] = useState<UnitOfMeasurementInterface[]>([])
  const categoriesTree = useTreeTilesStore((state) => state.categoriesTree)
  const nomenclatureGroupsTree = useTreeTilesStore(
    (state) => state.nomenclatureGroupsTree
  )
  const setCategoriesTree = useTreeTilesStore(
    (state) => state.setCategoriesTree
  )
  const setNomenclatureGroupsTree = useTreeTilesStore(
    (state) => state.setNomenclatureGroupsTree
  )

  const antdCategoriesTree = useMemo(
    () => toAntdTree(categoriesTree),
    [categoriesTree]
  )
  const antdNomenclatureGroupsTree = useMemo(
    () => toAntdTree(nomenclatureGroupsTree),
    [nomenclatureGroupsTree]
  )

  const reloadNomenclatureGroups = useCallback(async () => {
    try {
      setNomenclatureGroupsTree(await fetchNomenclatureGroupsTree())
    } catch (error) {
      toast.error('Ошибка загрузки групп номенклатуры')
    }
  }, [setNomenclatureGroupsTree])

  const reloadCategories = useCallback(async () => {
    try {
      setCategoriesTree(await fetchCategoriesTree())
    } catch (error) {
      toast.error('Ошибка загрузки категорий')
    }
  }, [setCategoriesTree])

  const hasFetchedTreesRef = useRef(false)

  useEffect(() => {
    if (hasFetchedTreesRef.current) return
    hasFetchedTreesRef.current = true
    if (!nomenclatureGroupsTree.length) reloadNomenclatureGroups()
    if (!categoriesTree.length) reloadCategories()
  }, [
    nomenclatureGroupsTree.length,
    categoriesTree.length,
    reloadNomenclatureGroups,
    reloadCategories,
  ])

  const reloadBrands = async () => {
    try {
      setBrands(await fetchBrands())
    } catch (error) {
      toast.error('Ошибка загрузки брендов')
    }
  }

  const loadBrands = async () => {
    if (!brands.length) await reloadBrands()
  }

  const loadCountries = async () => {
    if (countries.length) return
    try {
      setCountries(await fetchCountries())
    } catch (error) {
      toast.error('Ошибка загрузки стран')
    }
  }

  const loadUnits = async () => {
    if (units.length) return
    try {
      setUnits(await fetchUnitsOfMeasurement())
    } catch (error) {
      toast.error('Ошибка загрузки единиц измерения')
    }
  }

  return {
    brands,
    countries,
    units,
    categoriesTree,
    nomenclatureGroupsTree,
    antdCategoriesTree,
    antdNomenclatureGroupsTree,
    loadBrands,
    reloadBrands,
    loadCountries,
    loadUnits,
    reloadNomenclatureGroups,
    reloadCategories,
  }
}

export type NomenclatureDictionaries = ReturnType<
  typeof useNomenclatureDictionaries
>
